import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Badge,
  Banner,
  BlockStack,
  Box,
  Button,
  Card,
  DropZone,
  FormLayout,
  InlineStack,
  Page,
  Select,
  Spinner,
  Tabs,
  Text,
  TextField,
  Thumbnail,
} from '@shopify/polaris';
// NoteIcon removed — using a generic source string instead
import { useApi } from '../hooks/useApi';
import type { TemplateDetail, TriggerType } from '../types';
import PdfFieldEditor from '../components/PdfFieldEditor';
import ProductLinker from '../components/ProductLinker';
import SignersCard from '../components/SignersCard';
import { signerOptions, useMultiSignerAccess } from '../multiSigner';

type Mode = 'create' | 'edit';

const CUSTOMER_MESSAGE_HELP =
  'Optional. Shown to your customers in the signing email (and reminders) and at the top of the signing page.';

const TRIGGER_OPTIONS = [
  { label: 'Manual (merchant sends invite)', value: 'MANUAL' },
  { label: 'Auto on order created', value: 'ORDER_CREATED' },
  { label: 'Auto on order paid', value: 'ORDER_PAID' },
];

function statusBadge(status: string) {
  const tone = status === 'ACTIVE' ? 'success' : status === 'DRAFT' ? 'attention' : undefined;
  return <Badge tone={tone}>{status.charAt(0) + status.slice(1).toLowerCase()}</Badge>;
}

export default function TemplatePage() {
  const { id } = useParams<{ id: string }>();
  const mode: Mode = id ? 'edit' : 'create';
  const navigate = useNavigate();
  const { request, upload, getToken } = useApi();
  const multiSigner = useMultiSignerAccess();

  // ---- shared state ----
  const [template, setTemplate] = useState<TemplateDetail | null>(null);
  const [loading, setLoading] = useState(mode === 'edit');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  // ?tab=products opens the Linked products tab directly (from the Templates list).
  const [tab, setTab] = useState(() =>
    new URLSearchParams(window.location.search).get('tab') === 'products' ? 1 : 0,
  );

  // ---- create-mode upload form ----
  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [triggerType, setTriggerType] = useState<TriggerType>('MANUAL');

  // ---- edit-mode settings form ----
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editTrigger, setEditTrigger] = useState<TriggerType>('MANUAL');

  // Load existing template in edit mode.
  useEffect(() => {
    if (mode !== 'edit' || !id) return;
    (async () => {
      try {
        const data = await request<TemplateDetail>(`/templates/${id}`);
        setTemplate(data);
        setEditName(data.name);
        setEditDescription(data.description ?? '');
        setEditTrigger(data.triggerType);
      } catch (e: any) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [mode, id, request]);

  // ---- create: upload + create ----
  const handleCreate = async () => {
    if (!file || !name.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append('name', name.trim());
      if (description) fd.append('description', description);
      fd.append('triggerType', triggerType);
      fd.append('file', file);
      const created = await upload<TemplateDetail>('/templates', fd);
      navigate(`/templates/${created.id}/edit`);
    } catch (e: any) {
      setError(e.message);
      setSaving(false);
    }
  };

  // ---- edit: save settings ----
  const handleSaveSettings = async () => {
    if (!template) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await request<TemplateDetail>(`/templates/${template.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          name: editName,
          // '' clears the message.
          description: editDescription.trim(),
          triggerType: editTrigger,
        }),
      });
      setTemplate((prev) => prev ? { ...prev, ...updated } : prev);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  // ---- edit: activate / archive ----
  const handleActivate = async () => {
    if (!template) return;
    // Templates sent by hand only (Manual) can be sent without an order, so they need no products.
    if (template.productLinks.length === 0 && template.triggerType !== 'MANUAL') {
      setError('Link at least one product before activating — SignShield sends this contract only for orders that contain a linked product.');
      setTab(1);
      return;
    }
    try {
      const updated = await request<TemplateDetail>(`/templates/${template.id}/activate`, {
        method: 'POST',
      });
      setTemplate((prev) => prev ? { ...prev, ...updated } : prev);
    } catch (e: any) {
      setError(e.message);
    }
  };

  const handleSetDraft = async () => {
    if (!template) return;
    try {
      const updated = await request<TemplateDetail>(`/templates/${template.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ status: 'DRAFT' }),
      });
      setTemplate((prev) => prev ? { ...prev, ...updated } : prev);
    } catch (e: any) {
      setError(e.message);
    }
  };

  const handleArchive = async () => {
    if (!template) return;
    try {
      const updated = await request<TemplateDetail>(`/templates/${template.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ status: 'ARCHIVED' }),
      });
      setTemplate((prev) => prev ? { ...prev, ...updated } : prev);
    } catch (e: any) {
      setError(e.message);
    }
  };

  // ---- PDF URL helper (authenticated streaming) ----
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!template) return;
    // Build an object URL from the authenticated stream so react-pdf can load it.
    let revoked = false;
    (async () => {
      const token = await getToken();
      const res = await fetch(`/api/templates/${template.id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const blob = await res.blob();
      if (!revoked) setPdfUrl(URL.createObjectURL(blob));
    })();
    return () => { revoked = true; };
  }, [template?.id, getToken]);

  // ---- render: create mode ----
  if (mode === 'create') {
    const editorParams = new URLSearchParams({
      name: name.trim() || 'Untitled',
      ...(description ? { description } : {}),
      trigger: triggerType,
    });

    return (
      <Page
        title="New template"
        backAction={{ content: 'Templates', url: '/' }}
        primaryAction={{
          content: 'Create template',
          loading: saving,
          disabled: !file || !name.trim(),
          onAction: handleCreate,
        }}
      >
        <BlockStack gap="500">
          {error && <Banner tone="critical" onDismiss={() => setError(null)}>{error}</Banner>}

          <Card>
            <BlockStack gap="400">
              <Text variant="headingSm" as="h2">Template details</Text>
              <FormLayout>
                <TextField
                  label="Name"
                  value={name}
                  onChange={setName}
                  autoComplete="off"
                  placeholder="e.g. Service Agreement"
                />
                <TextField
                  label="Message to your customers"
                  value={description}
                  onChange={setDescription}
                  autoComplete="off"
                  multiline={3}
                  maxLength={1000}
                  placeholder="e.g. Please fill out this form before your appointment."
                  helpText={CUSTOMER_MESSAGE_HELP}
                />
                <Select
                  label="Trigger"
                  options={TRIGGER_OPTIONS}
                  value={triggerType}
                  onChange={(v) => setTriggerType(v as TriggerType)}
                  helpText="When should SignShield automatically send this template to customers?"
                />
              </FormLayout>
            </BlockStack>
          </Card>

          {/* ── Creation method choice ───────────────────────────────── */}
          <Text variant="headingSm" as="h2">How would you like to create this template?</Text>
          <InlineStack gap="400" wrap>

            {/* Option A: Upload PDF */}
            <div style={{ flex: 1, minWidth: 260 }}>
              <Card>
                <BlockStack gap="300">
                  <InlineStack gap="200" blockAlign="center">
                    <span style={{ fontSize: 22 }}>📄</span>
                    <Text variant="headingSm" as="h3">Upload PDF</Text>
                  </InlineStack>
                  <Text variant="bodySm" tone="subdued" as="p">
                    Upload an existing PDF contract. Then drag-and-drop signature fields
                    onto the PDF using the visual field editor.
                  </Text>
                  <DropZone
                    accept="application/pdf"
                    type="file"
                    allowMultiple={false}
                    onDrop={(_dropped, accepted) => accepted[0] && setFile(accepted[0])}
                  >
                    {file ? (
                      <InlineStack gap="300" blockAlign="center" wrap={false}>
                        <Thumbnail size="small" alt={file.name} source="" />
                        <BlockStack gap="100">
                          <Text variant="bodyMd" fontWeight="semibold" as="span">{file.name}</Text>
                          <Text variant="bodySm" tone="subdued" as="span">
                            {(file.size / 1024).toFixed(0)} KB
                          </Text>
                        </BlockStack>
                      </InlineStack>
                    ) : (
                      <DropZone.FileUpload
                        actionTitle="Add PDF"
                        actionHint="or drop a PDF file to upload (max 20 MB)"
                      />
                    )}
                  </DropZone>
                </BlockStack>
              </Card>
            </div>

            {/* Option B: Rich text editor */}
            <div style={{ flex: 1, minWidth: 260 }}>
              <Card>
                <BlockStack gap="300">
                  <InlineStack gap="200" blockAlign="center">
                    <span style={{ fontSize: 22 }}>✏️</span>
                    <Text variant="headingSm" as="h3">Create with Editor</Text>
                  </InlineStack>
                  <Text variant="bodySm" tone="subdued" as="p">
                    Write your contract in a rich text editor — add text, tables, images,
                    and place signature fields inline. Start from a blank document or choose
                    a starter template.
                  </Text>
                  <Button
                    disabled={!name.trim()}
                    onClick={() => navigate(`/templates/editor/new?${editorParams}`)}
                  >
                    Open Editor
                  </Button>
                  {!name.trim() && (
                    <Text variant="bodySm" tone="subdued" as="p">Enter a template name first.</Text>
                  )}
                </BlockStack>
              </Card>
            </div>

          </InlineStack>
        </BlockStack>
      </Page>
    );
  }

  // ---- render: edit mode ----
  if (loading) {
    return (
      <Page title="Loading…">
        <InlineStack align="center"><Spinner /></InlineStack>
      </Page>
    );
  }
  if (!template) return null;

  const tabs = [
    { id: 'fields', content: 'Field placement' },
    { id: 'products', content: 'Linked products' },
    { id: 'settings', content: 'Settings' },
  ];

  const secondaryActions = [];
  if (template.status === 'ACTIVE') {
    secondaryActions.push({ content: 'Archive', onAction: handleArchive, destructive: true });
  }

  return (
    <Page
      title={template.name}
      titleMetadata={statusBadge(template.status)}
      backAction={{ content: 'Templates', url: '/' }}
      primaryAction={
        template.status !== 'ACTIVE'
          ? { content: 'Activate', onAction: handleActivate }
          : undefined
      }
      secondaryActions={secondaryActions}
    >
      <BlockStack gap="400">
        {error && <Banner tone="critical" onDismiss={() => setError(null)}>{error}</Banner>}

        {template.productLinks.length === 0 && template.status !== 'ARCHIVED' && (template.triggerType === 'MANUAL' ? (
          <Banner tone="info" title="Sent by hand only">
            <p>
              No products are needed. Send it from <strong>Signing Sessions → Send a document</strong> (no order
              needed), or for an order from <strong>Send Signing Request (Manual)</strong>.
            </p>
          </Banner>
        ) : (
          <Banner
            tone="warning"
            title="No products linked — this contract will never be sent"
            action={tab !== 1 ? { content: 'Link products', onAction: () => setTab(1) } : undefined}
          >
            <p>SignShield sends this contract only for orders that contain a linked product. Orders without a linked product are ignored.</p>
          </Banner>
        ))}

        {multiSigner?.visible && template.status !== 'ARCHIVED' && (
          <SignersCard
            // Re-mount after a save so the list reflects the server's order.
            key={(template.signers ?? []).map((s) => s.id).join()}
            template={template}
            access={multiSigner}
            onSaved={(updated) => setTemplate((prev) => prev ? { ...prev, ...updated } : prev)}
            onSetDraft={handleSetDraft}
          />
        )}

        <Tabs tabs={tabs} selected={tab} onSelect={setTab}>
          {tab === 0 && template.sourceType === 'EDITOR' && (
            <Box paddingBlockEnd="300">
              <Banner
                tone="info"
                title="This is a written template — add or change fields in the text editor"
                action={{ content: 'Open text editor', onAction: () => navigate(`/templates/editor/${template.id}/edit`) }}
              >
                <p>
                  Its fields come from the text, so this tab shows a preview of where they appear in the PDF.
                  {(template.signers?.length ?? 0) > 0 && (
                    <> In the editor, pick "Fields for: 2. {template.signers![0].name}" to insert fields for another
                    signer. To move an existing field to another signer, remove it there and insert it again.</>
                  )}
                </p>
              </Banner>
            </Box>
          )}
          {tab === 0 && (
            <Card padding="0">
              {pdfUrl ? (
                <PdfFieldEditor
                  // Signer changes renumber fields on the server: reload the layout.
                  key={(template.signers ?? []).map((s) => `${s.id}:${s.position}`).join()}
                  templateId={template.id}
                  pdfUrl={pdfUrl}
                  pageCount={template.pageCount}
                  initialFields={template.fields}
                  onSaved={(fields) =>
                    setTemplate((prev) => prev ? { ...prev, fields } : prev)
                  }
                  signers={signerOptions(template.signers)}
                  signerEditable={template.sourceType !== 'EDITOR'}
                  readOnly={template.sourceType === 'EDITOR'}
                />
              ) : (
                <InlineStack align="center" blockAlign="center" gap="400">
                  <Spinner />
                  <Text as="p">Loading PDF…</Text>
                </InlineStack>
              )}
            </Card>
          )}

          {tab === 1 && (
            <Card>
              <ProductLinker
                templateId={template.id}
                initialLinks={template.productLinks}
                onSaved={(links) =>
                  setTemplate((prev) => prev ? { ...prev, productLinks: links } : prev)
                }
              />
            </Card>
          )}

          {tab === 2 && (
            <Card>
              <BlockStack gap="400">
                <Text variant="headingSm" as="h2">Settings</Text>
                <FormLayout>
                  <TextField
                    label="Name"
                    value={editName}
                    onChange={setEditName}
                    autoComplete="off"
                  />
                  <TextField
                    label="Message to your customers"
                    value={editDescription}
                    onChange={setEditDescription}
                    autoComplete="off"
                    multiline={3}
                    maxLength={1000}
                    placeholder="e.g. Please fill out this form before your appointment."
                    helpText={CUSTOMER_MESSAGE_HELP}
                  />
                  <Select
                    label="Trigger"
                    options={TRIGGER_OPTIONS}
                    value={editTrigger}
                    onChange={(v) => setEditTrigger(v as TriggerType)}
                  />
                </FormLayout>
                <InlineStack align="end">
                  <Button variant="primary" loading={saving} onClick={handleSaveSettings}>
                    Save settings
                  </Button>
                </InlineStack>
              </BlockStack>
            </Card>
          )}
        </Tabs>
      </BlockStack>
    </Page>
  );
}
